import type { FC} from 'react';
import { useCallback, useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Autocomplete, Button, CircularProgress, Unstable_Grid2 as Grid, TextField, Box, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { z as zod } from 'zod';

import type { MeetingType } from '@/types/meeting';
import type { Meeting, MeetingFile } from '@/types/v2/meeting';
import { config } from '@/config';
import { paths } from '@/paths';
import axios from '@/lib/v2/axios';
import { useUser } from '@/hooks/v2/use-user';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import ProgressButton from '@/components/common/buttons/progress-button';
import SubLayoutContainer from '@/components/sub-layout-container';
import ProfessionalDialog from '@/components/common/professional-dialog';
import { getPresignedUploadUrl, uploadFileToPresignedUrl } from '@/utils/s3';

import { toTitleCase } from '../../../utils/to-title-case';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { MeetingRoom } from '@mui/icons-material';

// Extend dayjs with UTC plugin
dayjs.extend(utc);
import { ChartBar, PresentationChart } from '@phosphor-icons/react';

const schema = zod.object({
  community_id: zod.string(),
  meeting_type_id: zod.number().min(1, 'Please select a meeting type'),
  held_on: zod.string()
    .min(1, 'Meeting date is required')
    .refine((date) => {
      if (!date || date.trim() === '') return false;
      const meetingDate = dayjs(date);
      return meetingDate.isValid();
    }, 'Please select a valid date and time')
    .refine((date) => {
      if (!date || date.trim() === '') return false;
      const meetingDate = dayjs(date);
      const now = dayjs();
      return meetingDate.isAfter(now.add(1, 'hour'), 'minute');
    }, 'Meeting must be scheduled at least 1 hour in advance'),
  agenda: zod.string().min(10, 'Meeting agenda must be at least 10 characters'),
});

type Values = zod.infer<typeof schema>;
type FileWithObject = File & { _objectUrl?: string };

interface CreateMeetingDialogProps {
  open: boolean;
  onClose: () => void;
}

const CreateMeetingDialog: FC<CreateMeetingDialogProps> = (props) => {
  const { open, onClose } = props;
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[] | null>(null);
  const [documentFile, setDocumentFile] = useState<FileWithObject | null>(null);
  const { user, community } = useUser();
  const navigate = useNavigate();

  const defaultValues: Values = {
    meeting_type_id: 0,
    held_on: '',
    agenda: '',
    community_id: community?.id || '',
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    register,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues,
    resolver: zodResolver(schema),
  });
  const createMeetingRequest = useMutation({
    mutationFn: async (values: Values & { files?: MeetingFile[] }) => {
      const response = await axios.post<{ meeting: Meeting; error?: string }>(
        `${config.site.api}/api/v2/communities/${community?.id}/meetings`,
        values
      );
      return response.data;
    },
  });

  const onSubmit = async (values: Values) => {
    try {
      let files: MeetingFile[] | undefined;
      if (documentFile) {
        const { url, key } = await getPresignedUploadUrl(documentFile);
        await uploadFileToPresignedUrl(url, documentFile);
        files = [{ key, file_name: documentFile.name }];
      }
      const response = await createMeetingRequest.mutateAsync({
        ...values,
        ...(files?.length ? { files } : {}),
      });
      if (response.error) {
        toast.error('Failed to create meeting');
        return;
      }

      navigate(`${paths.meetings.index}/${response?.meeting?.id}`);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Unable to create meeting');
    }
  };

  const handleClose = useCallback((): void => {
    setDocumentFile(null);
    onClose();
    reset();
  }, [onClose, reset]);

  useEffect(() => {
    const fetchMeetingTypes = async () => {
      try {
        const response = await axios.get(`${config.site.api}/api/v1/meetings/types`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });

        const data = response.data;
        setMeetingTypes(data);
      } catch (error) {
        console.error(error);
        setMeetingTypes([]);
        toast.error('Unable to fetch meeting types');
      }
    };

    fetchMeetingTypes();
  }, []);

  useEffect(() => {
    setValue('community_id', community?.id || '');
  }, [community, setValue]);

  return (
    <ProfessionalDialog
      actions={null}
      autoHeight
      contentSx={{ pt: 2 }}
      icon={<PresentationChart />}
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      subtitle="Schedule a new meeting for your community"
      title="Create Meeting"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <SubLayoutContainer>
            <Grid xs={6}>
              <Controller
                control={control}
                name="meeting_type_id"
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    fullWidth
                    getOptionLabel={(option) => toTitleCase(option.name)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(event, newValue) => {
                      onChange(newValue?.id || '');
                    }}
                    options={meetingTypes ?? []}
                    renderInput={(params) => (
                      <TextField
                        error={Boolean(errors.meeting_type_id)}
                        helperText={errors.meeting_type_id?.message}
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {!meetingTypes ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        label="Meeting Type"
                      />
                    )}
                    size="small"
                  />
                )}
              />
            </Grid>

            <Grid md={6} xs={12}>
              <Controller
                control={control}
                name="held_on"
                render={({ field }) => (
                  <DateTimePicker
                    label="Meeting Date & Time"
                    format="MMM D, YYYY hh:mm A"
                    minDateTime={dayjs().add(1, 'hour')} // Can't schedule in the past
                    maxDateTime={dayjs().add(1, 'year')} // Reasonable future limit
                    minutesStep={15} // 15-minute intervals
                    onChange={async (newValue) => {
                      if (newValue && dayjs(newValue).isValid()) {
                        // Store in UTC with proper timezone handling
                        const formattedDate = dayjs(newValue).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
                        field.onChange(formattedDate);
                        // Trigger validation after setting the value
                        await trigger('held_on');
                      } else {
                        field.onChange('');
                        await trigger('held_on');
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: Boolean(errors.held_on),
                        helperText: errors.held_on?.message || 'Select a date and time for the meeting',
                        placeholder: 'Choose meeting date and time',
                        sx: {
                          '& .MuiInputBase-input': {
                            cursor: 'pointer',
                          },
                        },
                      },
                      actionBar: {
                        actions: ['clear', 'today', 'cancel', 'accept'],
                      },
                      popper: {
                        sx: {
                          '& .MuiPaper-root': {
                            borderRadius: 2,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                          },
                        },
                      },
                    }}
                    value={field.value && field.value.trim() !== '' ? dayjs(field.value) : null}
                    views={['year', 'month', 'day', 'hours', 'minutes']}
                    openTo="day"
                    disablePast
                    ampm={true}
                    timezone="system"
                  />
                )}
              />
            </Grid>

            <Grid xs={12}>
              <TextField
                {...register('agenda')}
                error={Boolean(errors.agenda)}
                fullWidth
                helperText={errors.agenda?.message || 'Describe the meeting agenda and topics to discuss'}
                label="Meeting Agenda"
                multiline
                rows={4}
                size="small"
                placeholder="Enter meeting agenda, topics to discuss, or other relevant information..."
              />
            </Grid>

            <Grid xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Meeting document (optional)
              </Typography>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<ChartBar />}
              >
                {documentFile ? documentFile.name : 'Choose file'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.txt,image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    setDocumentFile(f ? (f as FileWithObject) : null);
                  }}
                />
              </Button>
              {documentFile ? (
                <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                  {documentFile.name}
                </Typography>
              ) : null}
            </Grid>
        </SubLayoutContainer>

        <Box display="flex" gap={2} justifyContent="flex-end" sx={{ pt: 2 }}>
          <Button onClick={handleClose} variant="outlined">Cancel</Button>
          <ProgressButton
            disabled={isSubmitting}
            loading={isSubmitting}
            loadingText="Creating..."
            size="medium"
            text="Create"
            type="submit"
            variant="contained"
          />
        </Box>
      </form>
    </ProfessionalDialog>
  );
};

export default CreateMeetingDialog;
